import { UseAuthToken } from "../../../../../../../libs/api/userSession";
import useSWR from "swr";
import {
  parseClientError,
  swrFetcher,
} from "../../../../../../../libs/api/Endpoints";
import s from "./Message.module.scss";
import { message, Skeleton } from "antd";
import { useQuery } from "../../../../../../../libs/common";
import { useNavigate } from "react-router-dom";
import { EnvelopeIcon } from "../../../../../../../global components/Icons/EnvelopeIcon";
import { PhoneIcon } from "../../../../../../../global components/Icons/PhoneIcon";
import MicrophoneIcon from "../../../../../../../global components/Icons/MicrophoneIcon";
import clsx from "clsx";
import { useMemo, useState } from "react";
// Define the shape of the state
type MsgType = "email" | "sms" | "mms" | "call";

export interface MessageDetailsType {
  id: string;
  title: string;
  type: "sms" | "mms" | "email" | "voice" | "omnichannel" | "voice_call";
  createdAt: string;
  sentAt: string | null;
  contactGroups: string | null;
  totalCredits: number;
  totalRecipients: number;
  uniqueRecipients: number;
  content: {
    ["email"]: {
      id: string;
      messageId: string;
      subject: string;
      replyTo: string;
      from: string;
      senderName: string;
      content: string;
      createdAt: string;
    };
    ["sms"]: {
      id: string;
      messageId: string;
      subject: string;
      replyTo: string;
      from: string;
      senderName: string;
      content: string;
      createdAt: string;
    };
    ["mms"]: {
      id: string;
      messageId: string;
      subject: string;
      replyTo: string;
      from: string;
      senderName: string;
      content: string;
      createdAt: string;
      mediaUrls: string[];
    };
    ["voice"]: {
      id: string;
      messageId: string;
      subject: string;
      replyTo: string;
      from: string;
      senderName: string;
      content: string;
      createdAt: string;
      mediaUrls: string[];
    };
    ["call"]: {
      id: string;
      messageId: string;
      subject: string;
      replyTo: string;
      from: string;
      senderName: string;
      content: string;
      createdAt: string;
      mediaUrls: string[];
    };
  };
  jsonFormat?: any;

  recipients: { contact: { email: string; id: string; phoneNo: string } }[];
  medias: {
    publicId: string;
    secureUrl: string;
    id: string;
  }[];
}

function MessageDetails() {
  const query = useQuery();
  const msgId = query.get("msgId");
  const navigate = useNavigate();
  const token = UseAuthToken();
  // Destructure the state with default values to avoid issues if the state is undefined
  const [activeMsgType, setActiveMsgType] = useState("email");
  type MessageContent =
    MessageDetailsType["content"][keyof MessageDetailsType["content"]];
  const {
    data: emailDetails,
    isLoading,
    error,
  } = useSWR<{ data: MessageDetailsType }, any, any>(
    token ? [`api/admin/messages/${msgId}`, token] : null,
    swrFetcher,
    {
      shouldRetryOnError: false, // Disable retrying on errors
    },
  );
  const toggles: {
    icon: React.ReactNode;
    label: string;
    type: MsgType;
  }[] = [
    {
      icon: <EnvelopeIcon className={s.icon} />,
      label: "Email",
      type: "email",
    },
    { icon: <PhoneIcon className={s.icon} />, label: "MMS", type: "mms" },
    { icon: <PhoneIcon className={s.icon} />, label: "SMS", type: "sms" },
    {
      icon: <MicrophoneIcon className={s.icon} />,
      label: "Voice",
      type: "call",
    },
  ];
  function convertResponseToMMS(response: {
    body: string;
    medias: string[];
  }): string {
    // Extract the text body
    let htmlString = response.body;

    // If there are media items, convert them to <img> tags
    if (response.medias && response.medias.length > 0) {
      response.medias.forEach((media) => {
        const imgTag = `<img src="${media}" alt="image" />`;
        // Append each image below the body text
        htmlString = htmlString + `${imgTag}<br />`;
      });
    }

    return htmlString;
  }

  const msgData = useMemo<MessageContent | undefined>(() => {
    if (!emailDetails?.data?.content) return undefined;

    // Use the state directly as the key
    const typeKey =
      activeMsgType.toLowerCase() as keyof MessageDetailsType["content"];
    return emailDetails.data.content[typeKey];
  }, [emailDetails, activeMsgType]);
  if (error) {
    const e = parseClientError(error);
    message.error(
      e.message.charAt(0).toUpperCase() + e.message.slice(1) ||
        "Message failed to load",
    );
  }
  return (
    <div className={s.mainWrapper}>
      <div className={s.wrapper}>
        <div className={s.msgHeader}>
          {isLoading || error ? (
            <Skeleton.Node
              active={isLoading || error}
              style={{ width: "100px", height: "30px" }}
            />
          ) : (
            <h2 className={s.title}>{emailDetails?.data.title}</h2>
          )}
          <button
            onClick={() =>
              navigate("contact", {
                state: {
                  msgID: msgId,
                },
              })
            }
            className={s.createBtn}
          >
            View contacts
          </button>
        </div>
        <div className={s.msg}>
          {isLoading || error ? (
            <Skeleton.Button
              active={isLoading || error}
              size={"default"}
              shape={"square"}
              block={true}
              style={{ height: "161px" }}
            />
          ) : (
            <table className={s.msgTitle}>
              {emailDetails?.data.type === "email" && (
                <tr>
                  <td className={s.emailTitles}>Email subject</td>
                  <td className={s.subtitles}>
                    {emailDetails?.data.content.email.subject}
                  </td>
                </tr>
              )}
              <tr>
                <td className={s.emailTitles}>Contact group</td>
                <td className={s.subtitles}>
                  {emailDetails?.data.contactGroups}
                </td>
              </tr>
              <tr>
                <td className={s.emailTitles}>Number of contacts</td>
                <td className={s.subtitles}>
                  {emailDetails?.data.totalRecipients}
                </td>
              </tr>
            </table>
          )}

          <div>
            <div className={s.msgBodyWrapper}>
              <h3>Message</h3>
              {emailDetails?.data.type === "omnichannel" && (
                <div className={clsx(s.toggles)}>
                  {toggles
                    .filter(
                      (toggle) => !!emailDetails?.data?.content?.[toggle.type],
                    )
                    .map((item, index) => (
                      <div
                        key={index}
                        className={clsx(s.toggle, {
                          // 3. This comparison now works because both are lowercase
                          [s.active]: item.type === activeMsgType,
                        })}
                        onClick={() => setActiveMsgType(item.type)}
                      >
                        {item.icon} <p className={s.toggleName}>{item.label}</p>
                      </div>
                    ))}
                </div>
              )}
            </div>
            {isLoading ? (
              <Skeleton.Button
                active={isLoading}
                size={"default"}
                shape={"square"}
                block={true}
                style={{ height: "449px" }}
              />
            ) : emailDetails?.data.type === "omnichannel" ? (
              <div className={s.msgBody}>
                {msgData ? (
                  <>
                    {/* CALL */}
                    {activeMsgType === "call" &&
                      "mediaUrls" in msgData &&
                      msgData.mediaUrls?.length > 0 && (
                        <audio
                          src={msgData.mediaUrls[0]}
                          controls
                          className={s.audio}
                        />
                      )}

                    {/* MMS */}
                    {activeMsgType === "mms" && "mediaUrls" in msgData && (
                      <div
                        dangerouslySetInnerHTML={{
                          __html: convertResponseToMMS({
                            body: msgData.content,
                            medias: msgData.mediaUrls || [],
                          }),
                        }}
                      />
                    )}

                    {/* EMAIL / SMS */}
                    {(activeMsgType === "email" || activeMsgType === "sms") && (
                      <div
                        dangerouslySetInnerHTML={{
                          __html: msgData.content,
                        }}
                      />
                    )}
                  </>
                ) : (
                  <p>No content available for this channel.</p>
                )}
              </div>
            ) : (
              <div className={s.msgBody}>
                {emailDetails?.data.type === "voice" ? (
                  <div className={s.audio}>
                    <audio
                      src={emailDetails?.data.content.voice.mediaUrls[0]}
                      controls
                    />
                  </div>
                ) : emailDetails?.data.type === "voice_call" ? (
                  <div className={s.audio}>
                    <audio
                      src={emailDetails.data.content.call.mediaUrls[0]}
                      controls
                    />
                  </div>
                ) : (
                  <div
                    dangerouslySetInnerHTML={{
                      __html:
                        emailDetails?.data.type === "mms"
                          ? convertResponseToMMS({
                              body: emailDetails?.data.content.mms.content,
                              medias: emailDetails.data.content.mms.mediaUrls,
                            })
                          : emailDetails?.data.type === "email"
                            ? emailDetails?.data.content.email.content
                            : emailDetails?.data?.content?.sms?.content!,
                    }}
                  ></div>
                )}
              </div>
            )}
            {/* Render rich text */}
          </div>
        </div>
      </div>
    </div>
  );
}
export default MessageDetails;
